/*
    Copyright (c) Computer-L.A.N. GmbH (http://www.computer-lan.de).
    Alle Rechte vorbehalten.
*/

import ArrayUtil from "./ArrayUtil";
import IByRefObject from "./IByRefObject";

export type Entries<T> = {
    [K in keyof T]: [K, T[K]];
}[keyof T][];

export default class AnyUtil {
    public static isNull<T>(wert: T | null): wert is null {
        return wert === null;
    }

    public static isUndefined<T>(wert: T | undefined): wert is undefined {
        return typeof wert === "undefined";
    }

    public static isNullOrUndefined<T>(wert: T | null | undefined): wert is null | undefined {
        return AnyUtil.isNull(wert) || AnyUtil.isUndefined(wert);
    }

    public static nameof<T>(name: Extract<keyof T, string>): string {
        return name;
    }

    public static keyOf<T>(key: Extract<keyof T, keyof T>): keyof T {
        return key;
    }

    public static deepCopy<T>(aThing: T): T {
        if (typeof aThing !== "object" || aThing === null) {
            return aThing;
        }

        // Objekte mit Interface "IByRefObject" werden als Referenz kopiert
        if (((aThing as unknown) as IByRefObject).isByRefObject) {
            return aThing;
        }

        const copiedThing: any = Array.isArray(aThing) ? [] : {};
        for (const key in aThing) {
            const nextThing = aThing[key];
            copiedThing[key] = AnyUtil.deepCopy(nextThing);
        }

        return copiedThing;
    }

    public static equals(value: any, otherValue: any, ignoreCase = false): boolean {
        if (Array.isArray(value) && Array.isArray(otherValue)) {
            return ArrayUtil.equals(value, otherValue, true);
        }

        if (typeof value === "object" && typeof otherValue === "object") {
            return AnyUtil.equalsObjects(value, otherValue);
        }

        if (typeof value === "string" && typeof otherValue === "string" && ignoreCase) {
            return value.toLowerCase() === otherValue.toLowerCase();
        }

        return value === otherValue;
    }

    public static deleteIgnoreTooltip(object: any) {
        delete object.ignoreTooltip;
        return object;
    }

    public static keys<T extends {}>(object: T): (keyof T)[] {
        return Object.keys(object) as any;
    }

    public static entries<T extends {}>(object: T): Entries<T> {
        return Object.entries(object) as any;
    }

    private static equalsObjects(object: any, otherObject: any): boolean {
        const equalsIfNull = AnyUtil.equalsIfNull(object, otherObject);
        if (equalsIfNull !== undefined) {
            return equalsIfNull;
        }

        // Bei Objekten mit dem Interface "IByRefObject" wird die Referenzen verglichen
        if ((((object as unknown) as IByRefObject).isByRefObject) && (((otherObject as unknown) as IByRefObject).isByRefObject)) {
            return object === otherObject;
        }

        return AnyUtil.equalsObjectsProperties(object, otherObject);
    }

    private static equalsObjectsProperties(object: any, otherObject: any): boolean {
        const objectPropertyNames = Object.getOwnPropertyNames(object).sort();
        const otherObjectPropertyNames = Object.getOwnPropertyNames(otherObject).sort();

        if (!ArrayUtil.equals(objectPropertyNames, otherObjectPropertyNames)) {
            return false;
        }

        for (const propertyName of objectPropertyNames) {
            if (!AnyUtil.equals(object[propertyName], otherObject[propertyName])) {
                return false;
            }
        }

        return true;
    }

    private static equalsIfNull(object: any, otherObject: any): boolean | undefined {
        if (!object && !otherObject) {
            return true;
        }

        if (!object || !otherObject) {
            return false;
        }

        return undefined;
    }
}

export const nameof = AnyUtil.nameof;
export const keyOf = AnyUtil.keyOf;
export const deepCopy = AnyUtil.deepCopy;
export const deleteIgnoreTooltip = AnyUtil.deleteIgnoreTooltip;
